import {Subscription} from "rxjs";
import {NgClass} from "@angular/common";
import {Component, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {FormArray, FormBuilder, FormGroup, ReactiveFormsModule} from "@angular/forms";

import {ToastrService} from "ngx-toastr";

import {
  OwnProfileResponse,
  UpsertUserNotificationSettingsRequest,
  UserNotificationSettingsResponse
} from "@numizmat/services-models/core";
import {UserNotificationSettingsApiService} from "@numizmat/services/core";
import {NotificationTypeResponse} from "@numizmat/services-models/nomenclature";
import {NotificationTypeApiService, NotificationTypeStateService} from "@numizmat/services/nomenclature";

@Component({
    selector: 'nz-notification-settings',
    imports: [
        ReactiveFormsModule,
        NgClass
    ],
    templateUrl: './notification-settings.component.html',
    styleUrl: './notification-settings.component.scss'
})
export class NotificationSettingsComponent implements OnInit, OnDestroy {
  private formBuilder: FormBuilder = inject(FormBuilder);
  private toastrService: ToastrService = inject(ToastrService);
  private notificationTypeApiService: NotificationTypeApiService = inject(NotificationTypeApiService);
  private notificationTypeStateService: NotificationTypeStateService = inject(NotificationTypeStateService);
  private userNotificationSettingsApiService: UserNotificationSettingsApiService = inject(UserNotificationSettingsApiService);

  private subs: Subscription = new Subscription();

  protected loading: boolean = true;
  protected notificationForm!: FormGroup;
  protected notificationTypes: NotificationTypeResponse[] = [];
  protected notificationSettings: UserNotificationSettingsResponse[] = [];

  public ngOnInit(): void {
    this.getAllNotificationTypes();
    this.getAllUserNotificationSettings();
  }

  public ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private getAllNotificationTypes(): void {
    const subscription: Subscription = this.notificationTypeStateService.getAllNotificationTypes().subscribe({
      next: notificationTypes => {
        if (!!notificationTypes) {
          this.notificationTypes = notificationTypes;
          this.createNotificationForm();
        } else {
          this.notificationTypeApiService.getAllNotificationTypes().subscribe({
            next: response => {
              this.notificationTypeStateService.setAllNotificationTypes(response);
            }
          });
        }
      }
    });
    this.subs.add(subscription);
  }

  private getAllUserNotificationSettings(): void {
    this.userNotificationSettingsApiService.getAllUserNotificationSettings().subscribe({
      next: response => {
        this.notificationSettings = response;
        this.createNotificationForm();
      }
    });
  }

  private createNotificationForm(): void {
    if (this.notificationTypes.length === 0 || this.notificationSettings.length === 0) {
      return;
    }
    this.notificationForm = this.formBuilder.group({
      notifications: this.formBuilder.array(
        this.notificationTypes.map(type => {
          return this.formBuilder.group({
            notificationTypeId: type.notificationTypeId,
            viaEmail: this.notificationSettings.find(x => x.notificationTypeId === type.notificationTypeId)?.viaEmail || false,
            viaPlatform: this.notificationSettings.find(x => x.notificationTypeId === type.notificationTypeId)?.viaPlatform || false,
          })
        })
      )
    });

    this.notificationsArray.controls.forEach((control, index) => {
      const subscription: Subscription = control.valueChanges.subscribe({
        next: (notificationSettings: any) => {
          const request: UpsertUserNotificationSettingsRequest = {
            notificationTypeId: notificationSettings.notificationTypeId,
            viaEmail: notificationSettings.viaEmail,
            viaPlatform: notificationSettings.viaPlatform
          };
          this.userNotificationSettingsApiService.upsertUserNotificationSettings(request).subscribe({
            next: response => {
              if (!!response) {
                this.toastrService.success('Успешно променихте настройките на известията!');
              }
            }
          });
        }
      });
      this.subs.add(subscription);
    });
    this.loading = false;
  }

  get notificationsArray(): FormArray<FormGroup> {
    return this.notificationForm.get('notifications') as FormArray;
  }
}
